import { get, isNil, size, find, indexOf, findIndex, differenceBy } from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { TRANSFORMATION_STACK_DTO_TYPE } from 'app/store/__generated__/types';

import { OperationType } from 'app/lib/constants';
import { selectProject, selectProjectUid } from 'app/store/projects/selectors.js';
import {
    selectView,
    selectSourceSchemaForCurrentView,
    selectSchemasForCurrentView,
    selectOperationsForCurrentView
} from 'app/store/view/selectors.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';

const getProps = (_, props) => props;

export const selectSelectedOperationIndex = get('operations.selectedIndex');
export const selectOperationError = get('operations.error');
const selectCreationDetails = get('operations.creating');

export const selectOperationInputUid = get('operations.inputNodeId');
export const selectOperationOutputUids = get('operations.outputNodeIds');
export const selectIsAppendToStackEnabled = get('operations.appendToStack');
export const selectIsKeepOpenEnabled = get('operations.keepOpen');

export const selectOperationInputEntityId = createSelector(
    selectProjectUid,
    selectOperationInputUid,
    (projectUid, nodeUid) => (projectUid && nodeUid ? `${projectUid}.${nodeUid}` : null)
);

export const selectOperationInputEntity = createSelector(
    selectOperationInputEntityId,
    selectEntitiesById,
    (entityId, entities) => (entityId ? entities[entityId] : null)
);

export const selectOperationInputSchema = createSelector(
    selectOperationInputUid,
    selectProject,
    (inputUid, project) => get(['schemasByUid', inputUid, 'columns'], project)
);

export const selectIsEditingOperation = createSelector(
    selectSelectedOperationIndex,
    selectCreationDetails,
    (index, creating) => !isNil(index) || !isNil(creating)
);

export const selectOperationDetails = createSelector(
    selectOperationsForCurrentView,
    selectSelectedOperationIndex,
    selectCreationDetails,
    (operations, index, details) => operations[index] ?? details
);

export const selectOperationDetailsType = createSelector(selectOperationDetails, get('_type'));

export const selectIsUpdatingOperation = createSelector(
    selectSelectedOperationIndex,
    (operationIndex) => !isNil(operationIndex)
);

export const selectIsEditingFormulaOperation = createSelector(
    selectIsEditingOperation,
    selectCreationDetails,
    selectOperationDetails,
    (isEditingOperation, creationDetails, details) =>
        isEditingOperation && (creationDetails || details)._type === OperationType.FORMULA
);

export const selectSelectedOperationId = createSelector(selectOperationDetails, get('uid'));

export const selectSchemaForCurrentOperation = createSelector(
    selectSourceSchemaForCurrentView,
    selectSchemasForCurrentView,
    selectSelectedOperationIndex,
    (sourceSchema, operationSchemas, selectedOperationIndex) => {
        const schemas = [sourceSchema, ...operationSchemas];
        const index = selectedOperationIndex ?? schemas.length - 1;
        return schemas[index];
    }
);

const selectPropOperationUid = (_, props) => props.operationUid;

export const selectOperationByUid = createSelector(
    selectOperationsForCurrentView,
    selectView,
    selectPropOperationUid,
    (operations, view, operationUid) => {
        if (isNil(view)) {
            return null;
        }

        const firstOperationInError = view?.error?.operationIndex;
        const operationIndex = findIndex({ uid: operationUid }, operations);
        const isOperationInError = operationIndex >= firstOperationInError;
        const operation = operations[operationIndex];

        return {
            ...operation,
            isOperationInError
        };
    }
);

export const selectColumnCountForOperation = createSelector(
    selectSourceSchemaForCurrentView,
    selectSchemasForCurrentView,
    getProps,
    (sourceSchema, operationSchemas, props) => {
        if (props.index === 0) {
            return size(sourceSchema);
        }

        return size(operationSchemas[props.index - 1]);
    }
);

export const selectIsExecuting = get('operations.isExecuting');

export const selectIsOperationsMenuOpen = get('operations.isMenuOpen');

export const selectOperationsMenuAttachTo = (state) =>
    state.operations.menuAttachTo ?? 'create-transform-handle';

// depends on selectedIndex which is only set when editing an operation
export const selectPreviousViewUuid = createSelector(
    [selectOperationInputEntityId, selectOperationDetails, selectView, selectProject],
    (inputId, operation, view, space) => {
        if (inputId) {
            return inputId;
        }

        if (!view) {
            return null;
        }

        if (view._type === TRANSFORMATION_STACK_DTO_TYPE && !isNil(operation?.uid)) {
            const operationIndex = indexOf(operation.uid, view.operationUids);

            if (operationIndex === 0) {
                const dependency = find(
                    (d) => d.isSource && d.fromView === view.uid,
                    space.dependencies
                );

                return `${view.spaceUid}.${dependency.toView}`;
            }

            return `${view.spaceUid}.${view.operationUids[operationIndex - 1]}`;
        }

        return view.entityId;
    }
);

export const selectViewTransformationErrors = createSelector(
    selectView,
    selectSelectedOperationIndex,
    (view, index) => (view?.error?.operationIndex === index ? view.error.errors : null)
);

export const selectColumnsAddedByOperation = createSelector(
    selectSourceSchemaForCurrentView,
    selectSchemaForCurrentOperation,
    (sourceSchema, currentSchema) => differenceBy('name', currentSchema, sourceSchema)
);
